import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import * as Mui from "@mui/material";
import type { WithStyles } from "type/tss";
import { useSelector } from "react-redux";
import { useAppThunkDispatch, RootState } from "redux/store";
import { Office } from "model/office";
import { OfficeBoard } from "model/office-board";
import { updateOffice } from "redux/slice/offices";
import { createOfficeBoard, deleteOfficeBoard, updateOfficeBoard } from "redux/slice/office-boards";
import { getBoardsForUser, getOfficeBoardsForOffice } from "redux/selector";
import { LabeledField } from "component/shared/profile-refresh/labeled-field";
import { styles } from "./styles";
import { TabHandle } from "component/shared/profile-refresh/tab-handle";

interface Props extends WithStyles<typeof styles> {
	office: Office;
	onDirtyChange: () => void;
}

const PHONE_PLACEHOLDER = "(000) 000-0000";

export const ProfileTab = forwardRef<TabHandle, Props>(({ office, classes = {}, onDirtyChange }, ref) => {
	const dispatch = useAppThunkDispatch();
	const boards = useSelector(getBoardsForUser);
	const initialBoards = useSelector((state: RootState) => getOfficeBoardsForOffice(state, office.id));

	const [name, setName] = useState(office.name || "");
	const [email, setEmail] = useState(office.email || "");
	const [phone, setPhone] = useState(office.phone || "");
	const [fax, setFax] = useState(office.fax || "");
	const [address, setAddress] = useState(office.address || "");
	const [city, setCity] = useState(office.city || "");
	const [state, setStateVal] = useState(office.state || "");
	const [zip, setZip] = useState(office.zip || "");
	const [officeBoards, setOfficeBoards] = useState<OfficeBoard[]>(initialBoards);
	const [submitted, setSubmitted] = useState(false);
	const [boardsSaved, setBoardsSaved] = useState(false);

	// After a save, take the rows back from the store so saved rows get their ids and removed rows go.
	useEffect(() => {
		if (boardsSaved) {
			setOfficeBoards(initialBoards);
			setBoardsSaved(false);
		}
	}, [boardsSaved, initialBoards]);

	const boardsKey = (rows: OfficeBoard[]) =>
		rows.map((b) => `${b.id}:${b.boardId}:${b.officeCode}:${b.officeId}`).join("|");

	const isDirty = () =>
		name !== (office.name || "") ||
		email !== (office.email || "") ||
		phone !== (office.phone || "") ||
		fax !== (office.fax || "") ||
		address !== (office.address || "") ||
		city !== (office.city || "") ||
		state !== (office.state || "") ||
		zip !== (office.zip || "") ||
		boardsKey(officeBoards) !== boardsKey(initialBoards);

	useEffect(() => { onDirtyChange(); }, [
		onDirtyChange, name, email, phone, fax, address, city, state, zip, officeBoards,
	]);

	// Office Name previously gated submit without ever being validated, so a blank name silently
	// no-opped. The Figma marks both it and Email required, so both are validated here.
	const nameError = !name.trim();
	const emailError = !email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

	const addMlsRow = () =>
		setOfficeBoards([...officeBoards, { id: 0, officeCode: "", officeId: office.id, boardId: 0 }]);

	const updateMlsRow = (index: number, patch: Partial<OfficeBoard>) =>
		setOfficeBoards(officeBoards.map((ob, i) => (i === index ? { ...ob, ...patch } : ob)));

	// Existing rows are marked for deletion by clearing officeId; brand-new rows are just dropped.
	const removeMlsRow = (index: number) =>
		setOfficeBoards(
			officeBoards
				.map((ob, i) => (i === index ? { ...ob, officeId: 0 } : ob))
				.filter((ob) => !(ob.id === 0 && ob.officeId === 0))
		);

	useImperativeHandle(ref, () => ({
		isDirty,
		cancel: () => {
			setName(office.name || "");
			setEmail(office.email || "");
			setPhone(office.phone || "");
			setFax(office.fax || "");
			setAddress(office.address || "");
			setCity(office.city || "");
			setStateVal(office.state || "");
			setZip(office.zip || "");
			setOfficeBoards(initialBoards);
			setSubmitted(false);
		},
		save: async () => {
			setSubmitted(true);
			if (nameError || emailError) {
				return;
			}
			const action = await dispatch(updateOffice({
				office: {
					...office,
					name, email, phone, fax, address, city, state, zip,
				},
			}));
			const resource = (action as any).payload;
			if (resource && resource.id) {
				const officeId = resource.id;
				for (const ob of officeBoards.filter((b) => b.id && b.officeId && b.officeCode && b.boardId)) {
					await dispatch(updateOfficeBoard({ officeBoard: { ...ob, officeId } }));
				}
				for (const ob of officeBoards.filter((b) => b.id && !b.officeId)) {
					await dispatch(deleteOfficeBoard({ officeBoard: ob }));
				}
				for (const ob of officeBoards.filter((b) => !b.id && b.officeCode && b.boardId)) {
					await dispatch(createOfficeBoard({ officeBoard: { ...ob, officeId } }));
				}
				setBoardsSaved(true);
			}
		},
	}));

	const visibleMlsRows = officeBoards.filter((ob) => ob.officeId);

	return (
		<div>
			<div className={classes.section}>
				<Mui.Typography className={classes.sectionTitle}>General</Mui.Typography>
				<div className={classes.formColumn}>
					<div className={classes.fieldBlock}>
						<LabeledField
							label="Office Name"
							required
							value={name}
							onChange={setName}
							error={submitted && nameError}
							helperText="Office name is required."
						/>
					</div>
					<div className={classes.fieldBlock}>
						<LabeledField
							label="Email"
							required
							value={email}
							onChange={setEmail}
							error={submitted && emailError}
							helperText={email.trim() ? "Invalid email." : "Email is required."}
						/>
					</div>
					<div className={`${classes.fieldRow} ${classes.fieldBlock}`}>
						<LabeledField label="Office Phone" value={phone} onChange={setPhone} placeholder={PHONE_PLACEHOLDER} />
						<LabeledField label="Fax" value={fax} onChange={setFax} placeholder={PHONE_PLACEHOLDER} />
					</div>
					<div className={classes.fieldBlock}>
						<LabeledField label="Address" value={address} onChange={setAddress} />
					</div>
					<div className={`${classes.fieldRow} ${classes.fieldBlock}`}>
						<LabeledField label="City" value={city} onChange={setCity} />
						{/* Free text, not the Figma's dropdown: there is no canonical state list in this
						    repo, existing values are unnormalized, and Canadian boards use provinces. */}
						<LabeledField label="State" value={state} onChange={setStateVal} />
					</div>
					<div className={classes.fieldBlock}>
						<LabeledField label="ZIP Code" value={zip} onChange={setZip} />
					</div>
				</div>
			</div>

			<div className={classes.section}>
				<Mui.Typography className={classes.sectionTitle}>MLS IDs</Mui.Typography>
				<div className={classes.formColumn}>
					{visibleMlsRows.map((officeBoard) => {
						const index = officeBoards.indexOf(officeBoard);
						return (
							<div className={classes.mlsRow} key={`${officeBoard.id}-${index}`}>
								<div className={classes.mlsFields}>
									<LabeledField
										label="MLS"
										select
										value={officeBoard.boardId ? String(officeBoard.boardId) : ""}
										onChange={(value) => updateMlsRow(index, { boardId: parseInt(value) })}
									>
										{[...boards]
											.sort((a, b) => (a.shortName || "").localeCompare(b.shortName || ""))
											.map((board) => (
												<Mui.MenuItem key={board.id} value={String(board.id)}>
													{board.shortName}
												</Mui.MenuItem>
											))}
									</LabeledField>
									<LabeledField
										label="Office ID"
										value={officeBoard.officeCode || ""}
										onChange={(value) => updateMlsRow(index, { officeCode: value })}
									/>
								</div>
								<div className={classes.removeCell}>
									<Mui.Button
										className={classes.removeButton}
										disableRipple
										onClick={() => removeMlsRow(index)}
									>
										Remove
									</Mui.Button>
								</div>
							</div>
						);
					})}
					<Mui.Button className={classes.addButton} disableRipple onClick={addMlsRow}>
						+ Add MLS ID
					</Mui.Button>
				</div>
			</div>
		</div>
	);
});
